import { axiosInstance } from "@/lib/axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Department } from "./useDepartmentQueries";

export const useCreateDepartment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (departmentData: {
      name: string;
      company: string;
      description?: string;
    }) => {
      const { data } = await axiosInstance.post<{ data: Department }>(
        "/api/v1/departments",
        departmentData
      );
      return data.data;
    },
    onSuccess: (_, departmentData) => {
      queryClient.invalidateQueries({
        queryKey: ["departments", departmentData.company],
      });
    },
  });
};

export const useUpdateDepartment = () => {
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Partial<Department>;
    }) => {
      const { data: responseData } = await axiosInstance.patch<{
        data: Department;
      }>(`/api/v1/departments/${id}`, data);
      return responseData.data;
    },
  });
};

export const useDeleteDepartment = () => {
  return useMutation({
    mutationFn: async (departmentId: string) => {
      const { data } = await axiosInstance.delete(
        `/api/v1/departments/${departmentId}`
      );
      return data;
    },
  });
};

export const useActivateDepartment = () => {
  return useMutation({
    mutationFn: async (departmentId: string) => {
      const { data } = await axiosInstance.post(
        `/api/v1/departments/activate/${departmentId}`
      );
      return data;
    },
  });
};

export const useDeactivateDepartment = () => {
  return useMutation({
    mutationFn: async (departmentId: string) => {
      const { data } = await axiosInstance.post(
        `/api/v1/departments/deactivate/${departmentId}`
      );
      return data;
    },
  });
};

export const useAssignDepartmentAdmin = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      departmentId,
      employeeId,
    }: {
      departmentId: string;
      employeeId: string;
    }) => {
      // Ensure the endpoint matches exactly what the backend expects
      const { data } = await axiosInstance.post(
        `/api/v1/departments/assign-admin`,
        {}, // Empty body since we're using query parameters
        {
          params: {
            departmentId,
            employeeId,
          },
        }
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["departments"] });
      queryClient.invalidateQueries({ queryKey: ["companyEmployees"] });
      queryClient.invalidateQueries({ queryKey: ["loggedInCompany"] });
    },
  });
};

export const useRevokeDepartmentAdmin = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      departmentId,
      employeeId,
    }: {
      departmentId: string;
      employeeId: string;
    }) => {
      const { data } = await axiosInstance.post(
        `/api/v1/departments/revoke-admin`,
        {}, // Empty body since we're using query parameters
        {
          params: {
            departmentId,
            employeeId,
          },
        }
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["departments"] });
      queryClient.invalidateQueries({ queryKey: ["companyEmployees"] });
      queryClient.invalidateQueries({ queryKey: ["loggedInCompany"] });
    },
  });
};

export const useAddEmployeeToDepartment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      departmentId,
      employeeId,
    }: {
      departmentId: string;
      employeeId: string;
    }) => {
      const { data } = await axiosInstance.post(
        `/api/v1/departments/add-employee`,
        {}, // Empty body since we're using query parameters
        {
          params: {
            departmentId,
            employeeId,
          },
        }
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["departments"] });
      queryClient.invalidateQueries({ queryKey: ["companyEmployees"] });
      queryClient.invalidateQueries({ queryKey: ["loggedInCompany"] });
    },
  });
};

export const useRemoveEmployeeFromDepartment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      departmentId,
      employeeId,
    }: {
      departmentId: string;
      employeeId: string;
    }) => {
      const { data } = await axiosInstance.post(
        `/api/v1/departments/remove-employee`,
        {}, // Empty body since we're using query parameters
        {
          params: {
            departmentId,
            employeeId,
          },
        }
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["departments"] });
      queryClient.invalidateQueries({ queryKey: ["companyEmployees"] });
      queryClient.invalidateQueries({ queryKey: ["loggedInCompany"] });
    },
  });
};

export const useTerminateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const { data } = await axiosInstance.delete(
        `/api/v1/users/terminate/${userId}`
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["departments"] });
      queryClient.invalidateQueries({ queryKey: ["companyEmployees"] });
      queryClient.invalidateQueries({ queryKey: ["loggedInCompany"] });
    },
  });
};
